import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from 'lib/api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { usePopup } from 'context/PopupProvider';
import { Lock, RefreshCw, User, Users, Eye, ToggleLeft, ToggleRight } from 'lucide-react';
import { PRIVATE_TABLE_STATUS, PRIVATE_TABLE_STATUS_LABEL, labelFor } from 'constants/enums';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TH_NO_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';

const money = (v: any) => parseFloat(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
const dateTime = (v: any) => (v ? new Date(v).toLocaleString() : '—');

const PrivateTableManagement = () => {
  const { confirm } = usePopup();
  const navigate = useNavigate();
  const [privateTables, setPrivateTables] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [message, setMessage] = useState('');

  const fetchPrivateTables = () => {
    setLoading(true);
    api.get(API_ENDPOINTS.ADMIN.PRIVATE_TABLES.GET_ALL)
      .then(res => {
        if (res.success && res.data) {
          const raw: any = res.data;
          const items = Array.isArray(raw) ? raw : (raw.items || []);
          setPrivateTables(items);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch private tables:', err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchPrivateTables();
  }, []);

  const handleToggleStatus = async (room: { id: number; status: number; table_name?: string; room_code?: string }) => {
    const isActive = Number(room.status) === PRIVATE_TABLE_STATUS.ACTIVE;
    if (isActive) {
      const confirmed = await confirm({
        title: 'Close Private Table',
        message: 'Close this private table? Active players will be disconnected and the room will be de-allocated.',
        variant: 'danger',
        confirmLabel: 'Close Table',
        cancelLabel: 'Cancel',
      });
      if (!confirmed) return;
    }

    setTogglingId(room.id);
    setMessage('');

    api.post(API_ENDPOINTS.ADMIN.PRIVATE_TABLES.TOGGLE_STATUS(room.id), {})
      .then(res => {
        setTogglingId(null);
        if (res.success) {
          setMessage(isActive ? 'Private table closed successfully.' : 'Private table reactivated successfully.');
          fetchPrivateTables();
        } else {
          setMessage(res.message || 'Failed to update table status.');
        }
      })
      .catch(err => {
        setTogglingId(null);
        console.error('Toggle status error:', err);
        setMessage('Error updating private table status.');
      });
  };

  if (loading && privateTables.length === 0) return (
    <div className="flex h-64 items-center justify-center text-muted font-medium">
      <RefreshCw className="animate-spin mr-2" size={20} /> Loading private rooms directory...
    </div>
  );

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="bg-card p-6 rounded-2xl border border-line flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-amber-500/10 text-brand-gold rounded-xl">
            <Lock size={24} />
          </div>
          <div>
            <h3 className="text-xl font-bold text-heading">Private Tables</h3>
            <p className="text-sm text-muted">User-created private rooms. Use the status toggle to open or close a table.</p>
          </div>
        </div>
        <button
          onClick={fetchPrivateTables}
          className="flex items-center gap-2 px-4 py-2 border border-line hover:bg-subtle rounded-lg text-sm text-body font-semibold transition-all shadow-sm"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh Rooms
        </button>
      </div>

      {message && (
        <div className={`px-4 py-3 rounded-xl text-sm font-semibold border ${
          message.includes('success') ? 'bg-green-500/10 text-green-700 border-green-200' : 'bg-red-500/10 text-brand-danger border-red-200'
        }`}>
          {message}
        </div>
      )}

      <div className="bg-card rounded-2xl shadow-sm border border-line overflow-hidden">
        <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between`}>
          <h4 className="font-bold text-heading">User-Created Rooms</h4>
          <span className="text-xs bg-subtle text-muted px-2.5 py-1 rounded-full font-semibold">
            {privateTables.filter(t => Number(t.status) === PRIVATE_TABLE_STATUS.ACTIVE).length} Active Tables
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className={LIST_TABLE_CLASS}>
            <thead className={LIST_THEAD_CLASS}>
              <tr>
                <th className={LIST_TH_NO_CLASS}>No.</th>
                {['Table', 'Room Code', 'Name', 'Variant', 'Currency', 'Min / Max Bet', 'Seats', 'Players', 'Total Volume', 'Games Played', 'Created By', 'Created At', 'Status', 'Action'].map((h, i) => (
                  <th key={i} className={LIST_TH_CLASS}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className={LIST_TBODY_CLASS}>
              {privateTables.length > 0 ? (
                privateTables.map((room, index) => {
                  const isActive = Number(room.status) === PRIVATE_TABLE_STATUS.ACTIVE;
                  const statusLabel = labelFor(PRIVATE_TABLE_STATUS_LABEL, room.status);
                  const isToggling = togglingId === room.id;

                  return (
                  <tr key={room.id} className="hover:bg-subtle/50 transition-colors">
                    <td className={LIST_TD_NO_CLASS}>{listRowNumber(1, index, 0)}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-bold text-heading`}>
                      <button
                        type="button"
                        onClick={() => navigate(`/private-tables/${room.id}`)}
                        className="font-bold text-heading hover:text-brand-gold hover:underline"
                        title="Open table details"
                      >
                        PT-{room.id}
                      </button>
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-mono font-semibold text-heading`}>{room.room_code || '—'}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm text-body`}>{room.table_name || '—'}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm text-body`}>
                      <div className="font-semibold text-heading">{room.variant_name || '—'}</div>
                      <div className="text-xs text-muted font-mono">
                        {room.variant_code || '—'}
                        {room.game_variant_id != null ? ` · #${room.game_variant_id}` : ''}
                      </div>
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm`}>{room.currency_symbol} {room.currency_code}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm text-body`}>{money(room.min_bet)} / {money(room.max_bet)}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm text-body`}>
                      <div className="flex items-center gap-1"><Users size={14} className="text-muted" /> {room.max_seats}</div>
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm text-body`}>{room.current_players ?? 0}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-semibold text-heading tabular-nums`}>
                      {room.currency_symbol || ''}{money(room.total_volume ?? 0)}
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-semibold text-heading tabular-nums`}>
                      {Number(room.total_battles ?? 0).toLocaleString()}
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm text-body`}>
                      <div className="flex items-center gap-1.5 font-medium">
                        <User size={14} className="text-muted shrink-0" />
                        {room.created_by ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/players/${room.created_by}/enforcement`)}
                            className="text-left text-heading hover:text-brand-gold hover:underline transition-colors"
                            title="Open player details"
                          >
                            {room.created_by_name || `User #${room.created_by}`}
                          </button>
                        ) : (
                          '—'
                        )}
                      </div>
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-xs text-muted`}>{dateTime(room.created_at)}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(room)}
                        disabled={isToggling}
                        className="inline-flex items-center gap-2 group disabled:opacity-50"
                        title={isActive ? 'Close private table' : 'Reactivate private table'}
                      >
                        {isActive ? (
                          <ToggleRight className="text-brand-gold shrink-0" size={28} />
                        ) : (
                          <ToggleLeft className="text-muted group-hover:text-heading shrink-0 transition-colors" size={28} />
                        )}
                        <span className={`text-xs font-bold uppercase tracking-wide ${
                          isActive ? 'text-emerald-700' : 'text-muted'
                        }`}>
                          {isToggling ? 'Updating…' : statusLabel}
                        </span>
                      </button>
                    </td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm`}>
                      <button
                        type="button"
                        onClick={() => navigate(`/private-tables/${room.id}`)}
                        className="inline-flex items-center justify-center p-2 rounded-lg border border-blue-200 bg-blue-500/10 text-blue-600 hover:bg-blue-500/15 hover:text-blue-700 transition-all"
                        title="View table details"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={15} className="px-6 py-10 text-center text-muted font-medium">
                    No user private tables located on game lobby servers.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PrivateTableManagement;
